import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { guardWrite } from '../../lib/modeGuard';
import { useAppMode } from '../../contexts/AppModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { capabilitiesFor } from '../../lib/permissions';
import * as teamApi from '../../lib/api/teamApi';
import { useTeamMembers } from '../../hooks/useTeamMembers';
import UserAccessPanel from './UserAccessPanel';
import {
  Users, User, Shield, Plus, Search, Mail, Stethoscope,
  CheckCircle2, Loader2, Trash2, Settings, Building2, Edit, AlertTriangle, RefreshCw, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = {
  medical: [
    { id: 'physician', label: 'Physician (MD/DO)', perms: 'Full clinical, prescribe, sign orders' },
    { id: 'np', label: 'Nurse Practitioner', perms: 'Clinical care, limited prescribe' },
    { id: 'pa', label: 'Physician Assistant', perms: 'Clinical care under physician supervision' },
    { id: 'rn', label: 'Registered Nurse', perms: 'Patient care, vitals, triage' },
    { id: 'ma', label: 'Medical Assistant', perms: 'Intake, vitals, rooming, documentation' },
  ],
  nonMedical: [
    { id: 'practice_admin', label: 'Practice Administrator', perms: 'Full admin, billing, HR, reports' },
    { id: 'office_manager', label: 'Office Manager', perms: 'Scheduling, staff, operations' },
    { id: 'front_desk', label: 'Front Desk', perms: 'Check-in, scheduling, phones' },
    { id: 'biller', label: 'Biller / Coder', perms: 'Claims, coding, payments, denials' },
    { id: 'scheduler', label: 'Scheduling Coordinator', perms: 'Appointments, waitlist, reminders' },
  ],
};

const APP_PERMS = [
  { app: 'Echo', modules: ['Front Desk', 'Scheduler', 'Patient Flow', 'Billing Overview'] },
  { app: 'Lumina', modules: ['Clinical Focus', 'Dictation', 'SOAP Notes', 'My Documents'] },
  { app: 'RevQ', modules: ['Claims', 'Denials', 'Analytics', 'Batch Scrub'] },
];

import { DEMO_TEAM_MEMBERS } from '../../lib/fixtures/teamMembersDemo';


const getRoleLabel = (roleId) => {
  const all = [...ROLES.medical, ...ROLES.nonMedical];
  return all.find(r => r.id === roleId)?.label || roleId;
};

const TeamsManager = () => {
  const { isDemo } = useAppMode();
  const { user } = useAuth();
  const { canTeamAdmin } = capabilitiesFor(user);
  // Demo mode bypasses RBAC (single-user fixture experience). Real mode enforces.
  const allowTeamAdmin = isDemo || canTeamAdmin;

  // ── Shared team store (single source of truth across Team + Roles tabs)
  // The adapter (`teamAdapter.js`) normalizes vendor + local payloads into
  // the same shape here. Both Settings → Team AND Settings → Roles read
  // from THIS hook, so mutations propagate instantly across the two tabs.
  const {
    members,
    loading,
    error: loadError,
    refresh,
    createMember: storeCreateMember,
    updateMember: storeUpdateMember,
    setRole: storeSetRole,
    setSchedulable: storeSetSchedulable,
    linkNpi: storeLinkNpi,
    unlinkNpi: storeUnlinkNpi,
    deleteMember: storeDeleteMember,
  } = useTeamMembers();

  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [npiLookup, setNpiLookup] = useState(false);
  const [npiLoading, setNpiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add member form state
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newCategory, setNewCategory] = useState('nonMedical');
  const [newNpi, setNewNpi] = useState('');
  const [newApps, setNewApps] = useState({ echo: true, lumina: false, revq: false });
  const [newAssignedTo, setNewAssignedTo] = useState('');

  // Silence unused warnings for fixtures & auxiliary hooks.
  // eslint-disable-next-line no-unused-expressions
  isDemo && DEMO_TEAM_MEMBERS;

  const fetchMembers = refresh;

  const medMembers = members.filter(m => m.category === 'medical' && m.status !== 'invited' && (m.name.toLowerCase().includes(search.toLowerCase()) || m.email.toLowerCase().includes(search.toLowerCase())));
  const nonMedMembers = members.filter(m => m.category === 'nonMedical' && m.status !== 'invited' && (m.name.toLowerCase().includes(search.toLowerCase()) || m.email.toLowerCase().includes(search.toLowerCase())));
  // Pending invites: backend returns invited users in the same /members
  // envelope with status === 'invited' (or is_active === false on the vendor
  // shape, already normalized by teamAdapter). Surface them in a dedicated
  // section so admins see invite state without hunting through statuses.
  const invitedMembers = members.filter(m => m.status === 'invited' && (m.name.toLowerCase().includes(search.toLowerCase()) || m.email.toLowerCase().includes(search.toLowerCase())));
  const physicians = members.filter(m => m.role === 'physician');

  const handleNpiLookup = async () => {
    if (!newNpi || newNpi.length < 10) { toast.error('Enter a valid 10-digit NPI'); return; }
    setNpiLoading(true);
    if (isDemo) {
      // Demo-only canned response
      setTimeout(() => {
        setNewName('Dr. Patricia Adams');
        setNewRole('physician');
        setNewCategory('medical');
        setNpiLoading(false);
        setNpiLookup(true);
        toast.success('NPI verified', { description: 'Dr. Patricia Adams — Endocrinology — Austin, TX' });
      }, 1200);
      return;
    }
    // Real mode: query NPPES via backend
    const res = await teamApi.listAvailableNpis({ search: newNpi });
    setNpiLoading(false);
    const match = res.ok ? (res.data?.npis || []).find(n => String(n.npi) === String(newNpi)) : null;
    if (match) {
      setNewName(match.name || '');
      setNewRole(match.role || 'physician');
      setNewCategory(match.category || 'medical');
      setNpiLookup(true);
      toast.success('NPI verified', { description: `${match.name || ''}${match.specialty ? ' — ' + match.specialty : ''}` });
    } else {
      setNpiLookup(false);
      toast.error('NPI not found', { description: res.ok ? 'No match in registry' : (res.error?.message || 'Request failed') });
    }
  };

  const handleAddMember = () => {
    if (!newName || !newEmail || !newRole) { toast.error('Fill in all required fields'); return; }
    guardWrite('Add team member', async () => {
      setSaving(true);
      const res = await storeCreateMember({
        name: newName, email: newEmail, role: newRole, category: newCategory,
        npi: newNpi || null, apps: newApps, assignedTo: newAssignedTo || null,
      });
      setSaving(false);
      if (res.ok) {
        toast.success(`${newName} invited`);
        resetForm();
        setAddOpen(false);
      } else {
        toast.error('Failed to add member', { description: res.error?.message });
      }
    }, isDemo);
  };

  const handleEditSave = () => {
    if (!editMember) return;
    guardWrite('Edit team member', async () => {
      setSaving(true);
      const original = members.find((m) => m.id === editMember.id) || {};
      const roleChanged = original.role !== editMember.role;
      const npiChanged = (original.npi || null) !== (editMember.npi || null);

      // 1) Dedicated role change (§14 vendor role mutation — shared store
      //    handles optimistic update + revert on failure, so both
      //    Settings → Team AND Settings → Roles tabs update instantly).
      if (roleChanged && editMember.role) {
        const rres = await storeSetRole(editMember.id, editMember.role);
        if (!rres.ok) {
          setSaving(false);
          toast.error('Role update failed', { description: rres.error?.message });
          return;
        }
      }
      // 2) Dedicated NPI link/unlink (§14 POST /npi, DELETE /npi)
      if (npiChanged) {
        const nres = editMember.npi
          ? await storeLinkNpi(editMember.id, editMember.npi)
          : await storeUnlinkNpi(editMember.id);
        if (!nres.ok) {
          setSaving(false);
          toast.error(editMember.npi ? 'NPI link failed' : 'NPI unlink failed', { description: nres.error?.message });
          return;
        }
      }
      // 3) Other fields via generic PUT (strip the ones we handled dedicated)
      const { role: _r, npi: _n, ...rest } = editMember;
      const res = await storeUpdateMember(editMember.id, rest);
      setSaving(false);
      if (res.ok) {
        toast.success(`${editMember.name} updated`);
        setEditMember(null);
      } else {
        toast.error('Failed to save', { description: res.error?.message });
      }
    }, isDemo);
  };

  const handleRemove = (id) => {
    guardWrite('Remove team member', async () => {
      setSaving(true);
      const res = await storeDeleteMember(id);
      setSaving(false);
      if (res.ok) {
        toast.success('Member removed');
        setEditMember(null);
      } else {
        toast.error('Failed to remove', { description: res.error?.message });
      }
    }, isDemo);
  };

  // Schedulable toggle — wired via shared store so the Scheduler provider
  // list (which filters on `is_schedulable`) also reflects the change.
  const handleToggleSchedulable = async (id, next) => {
    const res = await storeSetSchedulable(id, next);
    if (!res.ok) toast.error('Could not update schedulable', { description: res.error?.message });
  };

  const resetForm = () => {
    setNewName(''); setNewEmail(''); setNewRole(''); setNewCategory('nonMedical');
    setNewNpi(''); setNewApps({ echo: true, lumina: false, revq: false }); setNpiLookup(false); setNewAssignedTo('');
  };

  const MemberRow = ({ m }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`member-${m.id}`}>
      <div className={cn("h-9 w-9 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0", m.category === 'medical' ? 'bg-blue-600' : 'bg-slate-600')}>
        {m.avatar}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-slate-800 truncate">{m.name}</p>
          {m.status === 'invited' && <Badge className="text-[7px] border-0 bg-amber-50 text-amber-600">Invited</Badge>}
        </div>
        <p className="text-[10px] text-slate-400">{m.email}</p>
      </div>
      <div className="text-right shrink-0 space-y-0.5">
        <Badge variant="outline" className="text-[8px]">{getRoleLabel(m.role)}</Badge>
        {m.npi && <p className="text-[8px] text-slate-400">NPI: {m.npi}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {m.apps.echo && <Badge className="text-[7px] border-0 bg-slate-100 text-slate-500">Echo</Badge>}
        {m.apps.lumina && <Badge className="text-[7px] border-0 bg-blue-50 text-blue-600">Lumina</Badge>}
        {m.apps.revq && <Badge className="text-[7px] border-0 bg-emerald-50 text-emerald-600">RevQ</Badge>}
      </div>
      {m.assignedTo && (
        <Badge variant="outline" className="text-[8px] shrink-0">
          <Stethoscope className="h-2 w-2 mr-0.5" />
          {members.find(p => p.id === m.assignedTo)?.name?.split(' ').pop()}
        </Badge>
      )}
      {allowTeamAdmin && (
      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setEditMember({ ...m })} data-testid={`edit-member-${m.id}`}>
        <Edit className="h-3 w-3 text-slate-400" />
      </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="teams-manager">
      {/* ═══ USER ACCESS & ROLES (inline, collapsible; admin-only body, read-only banner for non-admins) ═══ */}
      <UserAccessPanel />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">Team Members</p>
          <p className="text-[10px] text-slate-400" data-testid="teams-members-count">{members.length} members · {medMembers.length} medical · {nonMedMembers.length} non-medical</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
            <input type="text" placeholder="Search members..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none w-48 focus:border-slate-400"
              data-testid="teams-search" />
          </div>
          {allowTeamAdmin && (
          <Button size="sm" className="text-xs gap-1.5 bg-[#111] hover:bg-[#222] h-8" onClick={() => { resetForm(); setAddOpen(true); }} data-testid="add-member-btn">
            <Plus className="h-3 w-3" /> Add Member
          </Button>
          )}
          {!allowTeamAdmin && (
            <Badge className="text-[10px] border-0 bg-slate-100 text-slate-500" data-testid="teams-readonly-badge">
              <Shield className="h-3 w-3 mr-1" /> Read-only (admin-only)
            </Badge>
          )}
        </div>
      </div>

      {/* ═══ LOADING / ERROR / EMPTY (real mode) ═══ */}
      {loading && (
        <div className="flex items-center justify-center py-12" data-testid="teams-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          <span className="ml-2 text-xs text-slate-500">Loading team members…</span>
        </div>
      )}
      {!loading && loadError && (
        <Card className={loadError.status === 403 ? 'border-amber-200 bg-amber-50/40' : 'border-red-200'} data-testid="teams-load-error">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className={`h-4 w-4 shrink-0 ${loadError.status === 403 ? 'text-amber-500' : 'text-red-500'}`} />
            <div className="flex-1">
              {loadError.status === 403 ? (
                <>
                  <p className="text-xs font-semibold text-amber-700" data-testid="teams-403-title">No permission to view team members</p>
                  <p className="text-[10px] text-amber-600">Your role does not include team admin access. Ask a workspace admin to grant the <code className="px-1 py-0.5 rounded bg-amber-100 text-amber-700">team:admin</code> capability or to upgrade your role.</p>
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold text-red-700">Couldn't load team members</p>
                  <p className="text-[10px] text-red-500">{loadError.message || 'Request failed'}</p>
                </>
              )}
            </div>
            {loadError.status !== 403 && (
              <Button size="sm" variant="outline" className="text-xs gap-1" onClick={fetchMembers} data-testid="teams-retry-btn">
                <RefreshCw className="h-3 w-3" /> Retry
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      {!loading && !loadError && members.length === 0 && (
        <div className="py-12 text-center" data-testid="teams-empty">
          <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-600">No team members yet</p>
          <p className="text-[11px] text-slate-400 mt-1">Invite your first member to get started.</p>
        </div>
      )}

      {/* ═══ MEDICAL STAFF ═══ */}
      {!loading && !loadError && members.length > 0 && (
      <>
      <div data-testid="medical-section">
        <div className="flex items-center gap-2 mb-2">
          <Stethoscope className="h-3.5 w-3.5 text-blue-500" />
          <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Medical Staff</p>
          <Badge className="text-[8px] border-0 bg-blue-50 text-blue-600">{medMembers.length}</Badge>
        </div>
        <div className="space-y-1.5">{medMembers.map(m => <MemberRow key={m.id} m={m} />)}</div>
      </div>

      {/* ═══ NON-MEDICAL STAFF ═══ */}
      <div data-testid="non-medical-section">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Non-Medical Staff</p>
          <Badge className="text-[8px] border-0 bg-slate-100 text-slate-500">{nonMedMembers.length}</Badge>
        </div>
        <div className="space-y-1.5">{nonMedMembers.map(m => <MemberRow key={m.id} m={m} />)}</div>
      </div>

      {/* ═══ PENDING INVITES ═══ */}
      {invitedMembers.length > 0 && (
        <div data-testid="invited-section">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-3.5 w-3.5 text-amber-500" />
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Pending Invites</p>
            <Badge className="text-[8px] border-0 bg-amber-50 text-amber-700">{invitedMembers.length}</Badge>
          </div>
          <div className="space-y-1.5">{invitedMembers.map(m => <MemberRow key={m.id} m={m} />)}</div>
        </div>
      )}
      </>
      )}

      {/* ═══ ADD MEMBER DIALOG ═══ */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="add-member-dialog">
          <DialogHeader className="px-6 pt-5 pb-3">
            <DialogTitle className="text-base font-semibold">Add Team Member</DialogTitle>
            <DialogDescription className="text-xs">Invite a new member and assign their role</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-5 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* NPI Lookup */}
            <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100">
              <p className="text-[10px] font-semibold text-blue-700 mb-1.5">NPI Lookup (for physicians)</p>
              <div className="flex gap-2">
                <input type="text" placeholder="Enter 10-digit NPI..." value={newNpi} onChange={e => setNewNpi(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-blue-200 text-xs outline-none focus:border-blue-400 bg-white"
                  data-testid="npi-input" />
                <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700 gap-1 h-9" onClick={handleNpiLookup} disabled={npiLoading} data-testid="npi-lookup-btn">
                  {npiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                  {npiLoading ? 'Looking up...' : 'Lookup'}
                </Button>
              </div>
              {npiLookup && isDemo && (
                <div className="mt-2 flex items-center gap-2 text-xs text-blue-700">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  <span>Dr. Patricia Adams — Endocrinology — 123 Medical Dr, Austin TX</span>
                </div>
              )}
              {npiLookup && !isDemo && newName && (
                <div className="mt-2 flex items-center gap-2 text-xs text-blue-700" data-testid="npi-result-real">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  <span>{newName}</span>
                </div>
              )}
            </div>

            {/* Category */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">Account Type</p>
              <div className="flex gap-2">
                {[{ id: 'medical', label: 'Medical', icon: Stethoscope }, { id: 'nonMedical', label: 'Non-Medical', icon: Building2 }].map(c => (
                  <button key={c.id} onClick={() => setNewCategory(c.id)}
                    className={cn("flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors",
                      newCategory === c.id ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600"
                    )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`cat-${c.id}`}>
                    <c.icon className="h-3 w-3" /> {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name & Email */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Full Name *</p>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Dr. Jane Doe"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400" data-testid="new-member-name" />
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Email *</p>
                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="jane@clinic.com"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400" data-testid="new-member-email" />
              </div>
            </div>

            {/* Staff Role (profile only — separate from access role in User Access panel) */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">Staff Role (profile only)</p>
              <div className="flex flex-wrap gap-1">
                {(newCategory === 'medical' ? ROLES.medical : ROLES.nonMedical).map(r => (
                  <button key={r.id} onClick={() => setNewRole(r.id)}
                    className={cn("px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors",
                      newRole === r.id ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`role-${r.id}`}>{r.label}</button>
                ))}
              </div>
            </div>

            {/* Assign to physician (for non-physician medical staff) */}
            {newCategory === 'medical' && newRole && newRole !== 'physician' && physicians.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">Assign to Physician</p>
                <div className="flex flex-wrap gap-1">
                  {physicians.map(p => (
                    <button key={p.id} onClick={() => setNewAssignedTo(p.id)}
                      className={cn("px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors",
                        newAssignedTo === p.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                      )} style={{ border: 'none', cursor: 'pointer' }}>{p.name}</button>
                  ))}
                </div>
              </div>
            )}

            {/* App Access */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">App Access</p>
              <div className="flex gap-2">
                {['echo', 'lumina', 'revq'].map(app => (
                  <button key={app} onClick={() => setNewApps(prev => ({ ...prev, [app]: !prev[app] }))}
                    className={cn("flex-1 py-2 rounded-lg text-[10px] font-medium transition-colors",
                      newApps[app] ? "bg-[#111] text-white" : "bg-slate-100 text-slate-500"
                    )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`app-toggle-${app}`}>{app.charAt(0).toUpperCase() + app.slice(1)}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-slate-100 gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222] gap-1" onClick={handleAddMember} disabled={saving} data-testid="confirm-add-member">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} {saving ? 'Inviting…' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ EDIT MEMBER DIALOG ═══ */}
      <Dialog open={!!editMember} onOpenChange={(v) => { if (!v) setEditMember(null); }}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="edit-member-dialog">
          {editMember && (
            <>
              <DialogHeader className="px-6 pt-5 pb-3">
                <DialogTitle className="text-base font-semibold">Edit {editMember.name}</DialogTitle>
              </DialogHeader>
              <div className="px-6 pb-5 space-y-3 max-h-[60vh] overflow-y-auto">
                {editMember.npi && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50">
                    <Shield className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-xs text-blue-700">NPI: {editMember.npi}</span>
                    {editMember.specialty && <Badge className="text-[8px] border-0 bg-blue-100 text-blue-700">{editMember.specialty}</Badge>}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] text-slate-500 mb-1">Name</p>
                    <input type="text" value={editMember.name} onChange={e => setEditMember({ ...editMember, name: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none" data-testid="edit-member-name" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 mb-1">Email</p>
                    <input type="text" value={editMember.email} onChange={e => setEditMember({ ...editMember, email: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none" />
                  </div>
                </div>
                {/* NPI (link/unlink) — medical staff only */}
                {editMember.category === 'medical' && (
                  <div>
                    <p className="text-[10px] text-slate-500 mb-1">NPI (leave empty to unlink)</p>
                    <input type="text" value={editMember.npi || ''} onChange={e => setEditMember({ ...editMember, npi: e.target.value || null })}
                      placeholder="10-digit NPI"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none" data-testid="edit-member-npi" />
                  </div>
                )}
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">Role</p>
                  <div className="flex flex-wrap gap-1">
                    {(editMember.category === 'medical' ? ROLES.medical : ROLES.nonMedical).map(r => (
                      <button key={r.id} onClick={() => setEditMember({ ...editMember, role: r.id })}
                        className={cn("px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors",
                          editMember.role === r.id ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600"
                        )} style={{ border: 'none', cursor: 'pointer' }}>{r.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">App Access</p>
                  <div className="flex gap-2">
                    {['echo', 'lumina', 'revq'].map(app => (
                      <button key={app} onClick={() => setEditMember({ ...editMember, apps: { ...editMember.apps, [app]: !editMember.apps[app] } })}
                        className={cn("flex-1 py-2 rounded-lg text-[10px] font-medium transition-colors",
                          editMember.apps[app] ? "bg-[#111] text-white" : "bg-slate-100 text-slate-500"
                        )} style={{ border: 'none', cursor: 'pointer' }}>{app.charAt(0).toUpperCase() + app.slice(1)}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">Status</p>
                  <div className="flex gap-2">
                    {['active', 'inactive'].map(s => (
                      <button key={s} onClick={() => setEditMember({ ...editMember, status: s })}
                        className={cn("px-3 py-1.5 rounded-lg text-[10px] font-medium transition-colors",
                          editMember.status === s ? (s === 'active' ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "bg-slate-100 text-slate-600"
                        )} style={{ border: 'none', cursor: 'pointer' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter className="px-6 py-3 border-t border-slate-100 gap-2">
                <Button variant="outline" size="sm" className="text-xs text-red-500 hover:text-red-700 gap-1" onClick={() => handleRemove(editMember.id)} data-testid="remove-member-btn">
                  <Trash2 className="h-3 w-3" /> Remove
                </Button>
                <div className="flex-1" />
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setEditMember(null)}>Cancel</Button>
                <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222]" onClick={handleEditSave} disabled={saving} data-testid="save-member-btn">{saving ? 'Saving…' : 'Save Changes'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsManager;
